import React from 'react'
import { startCase, toLower } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { SidePanelSummary } from 'components/SidePanel/SidePanelSummary'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useCustomerStatusQuery } from 'hooks/queries/Customers/useCustomerStatusQuery'

interface CustomerInfoRequiredProps {
  onboardingId: string
}

export const CustomerInfoRequired = ({ onboardingId }: CustomerInfoRequiredProps) => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } = useCustomerStatusQuery(onboardingId)

  if (isLoading) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (isError) {
    return (
      <Box paddingX={16} paddingY={32}>
        <RequestErrorStatusCard onReload={refetch} />
      </Box>
    )
  }

  const { data: infoDataItems, missingRoles, blockers } = data?.infoRequired || {}

  const uniqueCategoryRows = infoDataItems
    ?.filter((item, index, arr) => arr.findIndex(i => i.category === item.category) === index)
    .map(item => ({
      label: t(`customers.infoRequired.categories.${item.category}`, item.category),
      content: '',
    }))

  return (
    <Box flexCol gapY={16} padding={16}>
      {!!uniqueCategoryRows?.length && (
        <SidePanelSection heading={t('customers.infoRequired.details')}>
          <ul className="list-disc pl-5 text-body-regular-small">
            {uniqueCategoryRows.map(({ label }) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </SidePanelSection>
      )}
      {!!missingRoles?.length && (
        <SidePanelSection heading={t('customers.infoRequired.missingRoles')}>
          <ul className="list-disc pl-5 text-body-regular-small">
            {missingRoles.map(role => (
              <li key={role}>{startCase(toLower(role))}</li>
            ))}
          </ul>
        </SidePanelSection>
      )}
      {!!blockers?.length && (
        <SidePanelSection heading={t('customers.infoRequired.blockers')}>
          {blockers.map(blocker => (
            <SidePanelSummary
              key={blocker.code}
              rows={[
                { label: t('customers.infoRequired.code'), content: blocker.code },
                { label: t('customers.infoRequired.blockerMessage'), content: blocker.message },
              ]}
            />
          ))}
        </SidePanelSection>
      )}
    </Box>
  )
}
